import '@root/Components/Actions/actions.scss';
import { ActionIcon, Box, Divider, Drawer, Group, Title } from '@mantine/core';
import { ComponentType, useCallback, useEffect, useMemo, useState } from 'react';
import { useCompany } from '../Router/CompanyContent';
import { AnonymousJob } from '@apis/Resources';
import { useDi } from '@root/Services/DI';
import { ActivityItemAdapter, ActivityItemModel, JobStatus } from './ActivityPanel/ActivityTypes';
import { X } from 'tabler-icons-react';
import { TagActivityDetail } from './ActivityPanel/TagActivityDetails';
import { ActivityDetailsProps } from './ActivityPanel/Models';
import { GenericDetails } from './ActivityPanel/GenericDetails';
import { EventEmitter, useEvent, useEventValue } from '@root/Services/EventEmitter';
import { inject, singleton } from 'tsyringe';
import { JobService } from '@root/Services/Jobs/JobService';
import { ExportActivityDetails } from './ActivityPanel/ExportActivityDetails';
import { IdleStartStopActivityDetails } from './ActivityPanel/IdleStartStopActivityDetails';
import { CostForecastActivityDetails } from './ActivityPanel/CostForecastActivityDetails';
import { QuickbooksActivityDetails } from './ActivityPanel/QuickbooksActivityDetails';
import { useRouteBoundPortal } from '../Router/RouteBoundPortal';

@singleton()
export class ActivityDetailsPanelService {
    public readonly openRequested = new EventEmitter<AnonymousJob | undefined>(undefined);

    public constructor(@inject(JobService) private readonly jobService: JobService) {}

    public async openByJobId(jobId: string) {
        const result = await this.jobService.getJob(jobId);
        if (result) {
            this.openRequested.emit(result as AnonymousJob);
        }
    }
    public async open(job: AnonymousJob) {
        this.openRequested.emit(job);
    }
}

export function ActivityDetailsOpener(props: { item: AnonymousJob | undefined; onClose: () => void; cascade?: boolean }) {
    const { item, onClose, cascade } = props;
    const openerSvc = useDi(ActivityDetailsPanelService);
    const adapter = useDi(ActivityItemAdapter);
    const [details, setDetails] = useState<{ jobStatus: JobStatus; model: ActivityItemModel }>();
    const detailsCascade = useMemo(() => {
        const cascadeChange = EventEmitter.empty();
        const items: AnonymousJob[] = [];
        return {
            items,
            cascadeChange,
            push: (item: AnonymousJob) => {
                items.push(item);
                cascadeChange.emit();
            },
            pop: () => {
                items.pop();
                cascadeChange.emit();
            },
        };
    }, []);
    useEvent(detailsCascade.cascadeChange);

    const target = useRouteBoundPortal();
    const loadDetails = useCallback(
        async (job: AnonymousJob) => {
            const details = await adapter.loadActivityModel(job);
            setDetails(details);
        },
        [setDetails]
    );
    const loadRequested = useCallback((job: AnonymousJob | undefined) => {
        if (job) {
            detailsCascade.push(job);
        }
    }, []);
    useEvent(!cascade ? undefined : openerSvc.openRequested, loadRequested);
    useEffect(() => {
        if (item) {
            loadDetails(item);
        }
    }, [item?.Id, setDetails]);
    const close = useCallback(() => {
        onClose();
        setDetails(undefined);
    }, [onClose, setDetails]);
    return (
        <>
            <Drawer onClose={close} target={target} opened={!!details} position="right" size={500} withCloseButton={false}>
                <Box sx={{ display: 'flex', height: '100%', flexDirection: 'column' }}>
                    <Group position="apart" px="lg" sx={{ height: 80 }}>
                        <Title order={4}>{details ? details.model?.title : ''}</Title>
                        <ActionIcon onClick={close}>
                            <X />
                        </ActionIcon>
                    </Group>
                    <Divider />
                    {details ? <ActionDetailsLoader item={details?.jobStatus ?? undefined} displayInfo={details?.model} /> : null}
                </Box>
            </Drawer>
            {detailsCascade.items.map((item, i) => (
                <ActivityDetailsOpener key={i} item={item} onClose={detailsCascade.pop} />
            ))}
        </>
    );
}

const activityDetailsComponentLookup = new Map<string, ComponentType<ActivityDetailsProps>>([
    ['Cloudsaver.Resources.Domain.Models.TagResourcesJob', TagActivityDetail],
    ['Cloudsaver.Export.Application.Models.ExportRequestJob', ExportActivityDetails],
    ['Cloudsaver.Resources.Domain.Models.IdleResources.IdleResourcesStartStopJob', IdleStartStopActivityDetails],
    ['Cloudsaver.InvoiceIngestion.Application.Models.CostForecastRequested', CostForecastActivityDetails],
    ['Cloudsaver.InvoiceIngestion.Application.Models.UpdateQuickbooksInvoice', QuickbooksActivityDetails],
]);
function ActionDetailsLoader({ item, displayInfo }: ActivityDetailsProps) {
    const Component = activityDetailsComponentLookup.get(item.job.Type) ?? GenericDetails;
    return <Component item={item} displayInfo={displayInfo} />;
}
